import { useState } from 'react';
import { bulkChangeOccupancy } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const BulkOccupancyDialog = ({ open, onOpenChange, unitIds, onSuccess }) => {
  const [submitting, setSubmitting] = useState(false);
  const [occupancyStatus, setOccupancyStatus] = useState('vacant');

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await bulkChangeOccupancy({
        unit_ids: unitIds,
        occupancy_status: occupancyStatus,
      });
      if (result.success > 0) {
        toast.success(`${result.success} unit(s) occupancy updated`);
      }
      if (result.failed > 0) {
        toast.warning(`${result.failed} unit(s) failed to update`);
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(error.message || 'Bulk occupancy change failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (v) setOccupancyStatus('vacant');
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Bulk Change Occupancy</DialogTitle>
          <DialogDescription>
            Update occupancy status for {unitIds.length} selected unit{unitIds.length !== 1 ? 's' : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Occupancy Status</Label>
            <Select value={occupancyStatus} onValueChange={setOccupancyStatus}>
              <SelectTrigger data-testid="bulk-occupancy-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="vacant">Vacant</SelectItem>
                <SelectItem value="rented">Rented</SelectItem>
                <SelectItem value="owner_occupied">Owner Occupied</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            {occupancyStatus === 'vacant'
              ? 'This will end any active rentals/occupancies for the selected units.'
              : 'Note: For detailed tenant/owner contact info, use individual unit occupancy dialogs.'}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-bulk-occupancy">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Apply to {unitIds.length} Units
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
